import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixMultilingualTranslationContentV1TranslationContentUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/translation-content',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/translation-content',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/translation-content',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/translation-content',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_multilingual_translation-contents';

/**
 * Creates a translation content item.
 *
 * The content is validated based on the constraints defined in the corresponding schema.
 */
export function createContent(payload: object): RequestOptionsFactory<any> {
  function __createContent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'content.createdDate' },
          { path: 'content.updatedDate' },
          { path: 'content.fields.*.updatedDate' },
          { path: 'content.fields.*.richContent.metadata.createdTimestamp' },
          { path: 'content.fields.*.richContent.metadata.updatedTimestamp' },
          { path: 'content.fields.*.image.urlExpirationDate' },
          { path: 'content.fields.*.video.urlExpirationDate' },
          { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
          {
            path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'content.fields.*.video.posters.urlExpirationDate' },
          { path: 'content.fields.*.document.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'content.fields.*.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
          { path: 'content.fields.*.richContent.nodes.tocData.itemSpacing' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.CreateContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'content.createdDate' },
              { path: 'content.updatedDate' },
              { path: 'content.fields.*.updatedDate' },
              {
                path: 'content.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'content.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'content.fields.*.image.urlExpirationDate' },
              { path: 'content.fields.*.video.urlExpirationDate' },
              { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
              {
                path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'content.fields.*.video.posters.urlExpirationDate' },
              { path: 'content.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'content.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
              {
                path: 'content.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createContent;
}

/** Retrieves a translation content item. */
export function getContent(payload: object): RequestOptionsFactory<any> {
  function __getContent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'GET' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.GetContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/{contentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'content.createdDate' },
              { path: 'content.updatedDate' },
              { path: 'content.fields.*.updatedDate' },
              {
                path: 'content.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'content.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'content.fields.*.image.urlExpirationDate' },
              { path: 'content.fields.*.video.urlExpirationDate' },
              { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
              {
                path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'content.fields.*.video.posters.urlExpirationDate' },
              { path: 'content.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'content.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
              {
                path: 'content.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getContent;
}

/**
 * Updates a translation content item by ID.
 *
 * To remove a field, pass the field key with an empty object as the value. For example:
 *
 * ```json
 * {
 * "fields": {
 * "title": {}
 * }
 * }
 * ```
 */
export function updateContent(payload: object): RequestOptionsFactory<any> {
  function __updateContent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'content.createdDate' },
          { path: 'content.updatedDate' },
          { path: 'content.fields.*.updatedDate' },
          { path: 'content.fields.*.richContent.metadata.createdTimestamp' },
          { path: 'content.fields.*.richContent.metadata.updatedTimestamp' },
          { path: 'content.fields.*.image.urlExpirationDate' },
          { path: 'content.fields.*.video.urlExpirationDate' },
          { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
          {
            path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'content.fields.*.video.posters.urlExpirationDate' },
          { path: 'content.fields.*.document.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'content.fields.*.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
          { path: 'content.fields.*.richContent.nodes.tocData.itemSpacing' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'PATCH' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.UpdateContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/{content.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'content.createdDate' },
              { path: 'content.updatedDate' },
              { path: 'content.fields.*.updatedDate' },
              {
                path: 'content.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'content.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'content.fields.*.image.urlExpirationDate' },
              { path: 'content.fields.*.video.urlExpirationDate' },
              { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
              {
                path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'content.fields.*.video.posters.urlExpirationDate' },
              { path: 'content.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'content.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
              {
                path: 'content.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateContent;
}

/**
 * Updates a translation content item using a unique key, which is a combination of `content.schemaId`, `content.entityId`, and `content.locale`.
 *
 * To remove a field, pass the field key with an empty object as the value. For example:
 *
 * ```json
 * {
 * "fields": {
 * "title": {}
 * }
 * }
 * ```
 */
export function updateContentByKey(
  payload: object
): RequestOptionsFactory<any> {
  function __updateContentByKey({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'content.createdDate' },
          { path: 'content.updatedDate' },
          { path: 'content.fields.*.updatedDate' },
          { path: 'content.fields.*.richContent.metadata.createdTimestamp' },
          { path: 'content.fields.*.richContent.metadata.updatedTimestamp' },
          { path: 'content.fields.*.image.urlExpirationDate' },
          { path: 'content.fields.*.video.urlExpirationDate' },
          { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
          {
            path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'content.fields.*.video.posters.urlExpirationDate' },
          { path: 'content.fields.*.document.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'content.fields.*.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
          { path: 'content.fields.*.richContent.nodes.tocData.itemSpacing' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'PATCH' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.UpdateContentByKey',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/by-key',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'content.createdDate' },
              { path: 'content.updatedDate' },
              { path: 'content.fields.*.updatedDate' },
              {
                path: 'content.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'content.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'content.fields.*.image.urlExpirationDate' },
              { path: 'content.fields.*.video.urlExpirationDate' },
              { path: 'content.fields.*.video.resolutions.urlExpirationDate' },
              {
                path: 'content.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'content.fields.*.video.posters.urlExpirationDate' },
              { path: 'content.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'content.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'content.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'content.fields.*.richContent.nodes.tocData.fontSize' },
              {
                path: 'content.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateContentByKey;
}

/** Deletes a translation content item. */
export function deleteContent(payload: object): RequestOptionsFactory<any> {
  function __deleteContent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'DELETE' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.DeleteContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/{contentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteContent;
}

/**
 * Creates a query to retrieve a list of translation content items.
 *
 * The `queryContents()` function builds a query to retrieve a list of translation content items and returns a `ContentsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-builder/find) function.
 *
 * You can refine the query by chaining `ContentsQueryBuilder` functions onto the query. `ContentsQueryBuilder` functions enable you to filter, sort, and control the results that `queryContents()` returns.
 *
 * `queryContents()`  runs with the following `ContentsQueryBuilder` defaults which you can override:
 *
 * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-builder/limit)
 * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-builder/ascending)
 *
 * The following `ContentsQueryBuilder` functions are supported for `queryContents()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-result/items) property in `ContentsQueryResult`.
 */
export function queryContents(payload: object): RequestOptionsFactory<any> {
  function __queryContents({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.QueryContents',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contents.createdDate' },
              { path: 'contents.updatedDate' },
              { path: 'contents.fields.*.updatedDate' },
              {
                path: 'contents.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'contents.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'contents.fields.*.image.urlExpirationDate' },
              { path: 'contents.fields.*.video.urlExpirationDate' },
              { path: 'contents.fields.*.video.resolutions.urlExpirationDate' },
              {
                path: 'contents.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'contents.fields.*.video.posters.urlExpirationDate' },
              { path: 'contents.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'contents.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'contents.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'contents.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'contents.fields.*.richContent.nodes.tocData.fontSize' },
              {
                path: 'contents.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryContents;
}

/**
 * Retrieves a list of all translation content associated with a site that matches the search query, with optional data aggregation.
 *
 * Note that this method allows you to retrieve all translation content for a site, regardless of which app created it.
 *
 * Use this method to search translation content fields on a site for a given expression, and/or to perform data aggregations on a site's translation content fields.
 *
 *
 * To learn about working with *Search* methods, see _API Query Language_ ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language))
 * and *Sorting and Paging* ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-sort-array) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging)).
 */
export function searchContents(payload: object): RequestOptionsFactory<any> {
  function __searchContents({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.SearchContents',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'contents.createdDate' },
              { path: 'contents.updatedDate' },
              { path: 'contents.fields.*.updatedDate' },
              {
                path: 'contents.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'contents.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'contents.fields.*.image.urlExpirationDate' },
              { path: 'contents.fields.*.video.urlExpirationDate' },
              { path: 'contents.fields.*.video.resolutions.urlExpirationDate' },
              {
                path: 'contents.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'contents.fields.*.video.posters.urlExpirationDate' },
              { path: 'contents.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'contents.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'contents.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'contents.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'contents.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'contents.fields.*.richContent.nodes.tocData.fontSize' },
              {
                path: 'contents.fields.*.richContent.nodes.tocData.itemSpacing',
              },
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchContents;
}

/**
 * Creates multiple translation content items.
 *
 * The content items are validated based on the constraints defined in the corresponding schema.
 */
export function bulkCreateContent(payload: object): RequestOptionsFactory<any> {
  function __bulkCreateContent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'contents.createdDate' },
          { path: 'contents.updatedDate' },
          { path: 'contents.fields.*.updatedDate' },
          { path: 'contents.fields.*.richContent.metadata.createdTimestamp' },
          { path: 'contents.fields.*.richContent.metadata.updatedTimestamp' },
          { path: 'contents.fields.*.image.urlExpirationDate' },
          { path: 'contents.fields.*.video.urlExpirationDate' },
          { path: 'contents.fields.*.video.resolutions.urlExpirationDate' },
          {
            path: 'contents.fields.*.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'contents.fields.*.video.posters.urlExpirationDate' },
          { path: 'contents.fields.*.document.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'contents.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'contents.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'contents.fields.*.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'contents.fields.*.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'contents.fields.*.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'contents.fields.*.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'contents.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'contents.fields.*.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'contents.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'contents.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'contents.fields.*.richContent.nodes.tocData.fontSize' },
          { path: 'contents.fields.*.richContent.nodes.tocData.itemSpacing' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.BulkCreateContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/bulk/contents/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.fields.*.updatedDate' },
              {
                path: 'results.item.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'results.item.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'results.item.fields.*.image.urlExpirationDate' },
              { path: 'results.item.fields.*.video.urlExpirationDate' },
              {
                path: 'results.item.fields.*.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'results.item.fields.*.video.posters.urlExpirationDate' },
              { path: 'results.item.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateContent;
}

/**
 * Updates multiple translation content items by ID.
 *
 * To remove a field, pass the field key with an empty object as the value. For example:
 *
 * ```json
 * {
 * "fields": {
 * "title": {}
 * }
 * }
 * ```
 */
export function bulkUpdateContent(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateContent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'contents.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'contents.content.createdDate' },
          { path: 'contents.content.updatedDate' },
          { path: 'contents.content.fields.*.updatedDate' },
          {
            path: 'contents.content.fields.*.richContent.metadata.createdTimestamp',
          },
          {
            path: 'contents.content.fields.*.richContent.metadata.updatedTimestamp',
          },
          { path: 'contents.content.fields.*.image.urlExpirationDate' },
          { path: 'contents.content.fields.*.video.urlExpirationDate' },
          {
            path: 'contents.content.fields.*.video.resolutions.urlExpirationDate',
          },
          {
            path: 'contents.content.fields.*.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'contents.content.fields.*.video.posters.urlExpirationDate' },
          { path: 'contents.content.fields.*.document.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.tocData.itemSpacing',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.BulkUpdateContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/bulk/contents/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.fields.*.updatedDate' },
              {
                path: 'results.item.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'results.item.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'results.item.fields.*.image.urlExpirationDate' },
              { path: 'results.item.fields.*.video.urlExpirationDate' },
              {
                path: 'results.item.fields.*.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'results.item.fields.*.video.posters.urlExpirationDate' },
              { path: 'results.item.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateContent;
}

/**
 * Updates multiple translation content items using a unique key, which is a combination of `contents.content.schemaId`, `contents.content.entityId`, and `contents.content.locale`.
 *
 * To remove a field, pass the field key with an empty object as the value. For example:
 *
 * ```json
 * {
 * "fields": {
 * "title": {}
 * }
 * }
 * ```
 */
export function bulkUpdateContentByKey(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateContentByKey({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'contents.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'contents.content.createdDate' },
          { path: 'contents.content.updatedDate' },
          { path: 'contents.content.fields.*.updatedDate' },
          {
            path: 'contents.content.fields.*.richContent.metadata.createdTimestamp',
          },
          {
            path: 'contents.content.fields.*.richContent.metadata.updatedTimestamp',
          },
          { path: 'contents.content.fields.*.image.urlExpirationDate' },
          { path: 'contents.content.fields.*.video.urlExpirationDate' },
          {
            path: 'contents.content.fields.*.video.resolutions.urlExpirationDate',
          },
          {
            path: 'contents.content.fields.*.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'contents.content.fields.*.video.posters.urlExpirationDate' },
          { path: 'contents.content.fields.*.document.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.tocData.fontSize',
          },
          {
            path: 'contents.content.fields.*.richContent.nodes.tocData.itemSpacing',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.BulkUpdateContentByKey',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/bulk/contents/update-by-key',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.fields.*.updatedDate' },
              {
                path: 'results.item.fields.*.richContent.metadata.createdTimestamp',
              },
              {
                path: 'results.item.fields.*.richContent.metadata.updatedTimestamp',
              },
              { path: 'results.item.fields.*.image.urlExpirationDate' },
              { path: 'results.item.fields.*.video.urlExpirationDate' },
              {
                path: 'results.item.fields.*.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.fields.*.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'results.item.fields.*.video.posters.urlExpirationDate' },
              { path: 'results.item.fields.*.document.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.fields.*.richContent.nodes.tocData.itemSpacing',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateContentByKey;
}

/** Deletes multiple translation content items. */
export function bulkDeleteContent(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteContent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.BulkDeleteContent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/bulk/contents/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteContent;
}

/**
 * Gets word counts and field counts for translatable content across a site.
 *
 * This method provides detailed translation analytics showing translatable source content statistics and target language translation progress, including current translations and outdated translations. Statistics are provided at the site level and broken down by translation schema for granular progress tracking.
 *
 * Call this method when you need to track translation progress or build dashboards showing translation completion rates across different translation schemas.
 */
export function getContentWordCount(
  payload: object
): RequestOptionsFactory<any> {
  function __getContentWordCount({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.translation.v1.content',
      method: 'GET' as any,
      methodFqn:
        'wix.multilingual.translation.content.v1.TranslationContent.GetContentWordCount',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualTranslationContentV1TranslationContentUrl({
        protoPath: '/v1/contents/word-count/{sourceLanguage}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getContentWordCount;
}
